import EventEmitter from 'events'
import result from './introspection'
import * as Sentry from '@sentry/react'
import typePolicies from './TypePolicies'
import { WebSocketLink } from './WebSocket'

import { onError } from '@apollo/client/link/error'
import { getMainDefinition } from '@apollo/client/utilities'
import {
  split,
  HttpLink,
  ApolloLink,
  Observable,
  ApolloClient,
  InMemoryCache,
} from '@apollo/client'

export function createApolloClient(
  eventEmitter: EventEmitter,
  endpoint: string,
  wsEndpoint: string,
  getToken: () => Promise<string>
) {
  return new ApolloClient({
    connectToDevTools: true,
    cache: new InMemoryCache({
      addTypename: true,
      resultCaching: true,
      typePolicies,
      possibleTypes: result.possibleTypes,
    }),
    link: ApolloLink.from([
      new ApolloLink((operation, next) => {
        return new Observable((observer) => {
          getToken().then((token) => {
            operation.setContext({
              headers: { Authorization: token },
            })
            next(operation).subscribe(observer)
          })
        })
      }),
      onError(({ graphQLErrors, operation, forward }) => {
        if (graphQLErrors?.[0]?.extensions?.code === 'UNAUTHENTICATED') {
          return new Observable((observer) => {
            getToken().then((token) => {
              operation.setContext({
                headers: {
                  ...operation.getContext().headers,
                  Authorization: token,
                },
              })

              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              }

              forward(operation).subscribe(subscriber)
            })
          })
        }

        Sentry.captureException(graphQLErrors)
      }),
      split(
        ({ query }) => {
          const definition = getMainDefinition(query)
          return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
          )
        },
        new WebSocketLink({
          url: wsEndpoint,
          lazy: false,
          retryAttempts: 5,
          on: {
            connected() {
              eventEmitter.emit('____ws_ready')
            },
          },
          async connectionParams() {
            const token = await getToken()
            return {
              origin,
              kind: 'widget',
              authToken: token,
            }
          },
        }),
        new HttpLink({ uri: `${endpoint}/graphql` })
      ),
    ]),
  })
}
